import React, { Component } from "react";
import "./QueueLogsDashboardV3.css";
import { getInformationWithDataV3 } from '../../services/CABE.js';
// Menu
import { Menu } from "../../components/Menu/Menu";

/**
 * v3 clone of QueueLogsDashboard.js (original at /process-list-mas90, untouched).
 * Built as part of the 2026-08 SO reliability initiative:
 *   - points to /v3/queue/list instead of the v2 endpoint
 *   - adds a live "system health" strip (local-queue-worker / local-monitor-worker
 *     heartbeats, from system_process_heartbeat) so support can see at a glance
 *     whether the processing pipeline is actually alive
 *   - highlights rows stuck in claimed/processing past the same 5-min threshold
 *     local-queue-worker now uses to auto-reclaim them
 *   - shows requestedBy (who submitted it)
 */
export default class QueueLogsDashboardV3 extends Component {
  constructor(props) {
    super(props);

    const today = this.formatDateInput(new Date());
    const sevenDaysAgo = this.formatDateInput(this.addDays(new Date(), -7));

    this.state = {
      loading: false,
      error: "",
      logs: [],
      health: [],
      healthError: "",

      // filters
      dateStart: sevenDaysAgo,
      dateEnd: today,
      status: "",
      action: "",
      companyId: "",
      requestId: "",
      page: 1,
      pageSize: 50,

      // ui
      autoRefresh: false,
      autoRefreshSec: 10,
      selectedLog: null,
      showModal: false,
    };

    this._refreshTimer = null;
  }

  componentDidMount() {
    this.fetchLogs();
    this.fetchHealth();
  }

  componentWillUnmount() {
    this.clearAutoRefresh();
  }

  addDays(date, days) {
    const d = new Date(date.getTime());
    d.setDate(d.getDate() + days);
    return d;
  }

  formatDateInput(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return y + "-" + m + "-" + d;
  }

  setAutoRefresh(enabled) {
    this.setState({ autoRefresh: enabled }, () => {
      if (this.state.autoRefresh) {
        this.clearAutoRefresh();
        const ms = (Number(this.state.autoRefreshSec) || 10) * 1000;
        this._refreshTimer = setInterval(() => {
          this.fetchLogs(false);
          this.fetchHealth();
        }, ms);
      } else {
        this.clearAutoRefresh();
      }
    });
  }

  clearAutoRefresh() {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  async fetchHealth() {
    try {
      const resp = await getInformationWithDataV3('/v3/queue/system-health', {});
      if (resp.status.code !== 1) {
        this.setState({ healthError: "Error loading system health" });
        return;
      }
      this.setState({ health: resp.data || [], healthError: "" });
    } catch (err) {
      this.setState({ healthError: err && err.message ? err.message : String(err) });
    }
  }

  async fetchLogs(setLoading) {
    const shouldSetLoading = typeof setLoading === "undefined" ? true : setLoading;

    try {
      if (shouldSetLoading) this.setState({ loading: true, error: "" });

      const body = {
        dateStart: this.state.dateStart,
        dateEnd: this.state.dateEnd,
        status: this.state.status || undefined,
        action: this.state.action || undefined,
        companyId: this.state.companyId || undefined,
        page: this.state.page,
        pageSize: this.state.pageSize,
      };

      const resp = await getInformationWithDataV3('/v3/queue/list', body);

      if (resp.status.code !== 1) {
        const msg = (resp && (resp.error || (resp.status && resp.status.Description))) || "Error loading logs";
        this.setState({ loading: false, error: msg, logs: [] });
        return;
      }

      let rows = resp.data || [];

      const reqFilter = (this.state.requestId || "").trim();
      if (reqFilter) {
        rows = rows.filter((r) => String(r.requestId || "").indexOf(reqFilter) !== -1);
      }

      this.setState({ loading: false, logs: rows, error: "" });
    } catch (err) {
      this.setState({
        loading: false,
        error: err && err.message ? err.message : String(err),
        logs: [],
      });
    }
  }

  onChangeField(field, value) {
    const next = {};
    next[field] = value;
    if (
      field === "dateStart" ||
      field === "dateEnd" ||
      field === "status" ||
      field === "action" ||
      field === "companyId"
    ) {
      next.page = 1;
    }
    this.setState(next);
  }

  applyFilters() {
    this.setState({ page: 1 }, () => this.fetchLogs());
  }

  clearFilters() {
    const today = this.formatDateInput(new Date());
    const sevenDaysAgo = this.formatDateInput(this.addDays(new Date(), -7));

    this.setState(
      {
        dateStart: sevenDaysAgo,
        dateEnd: today,
        status: "",
        action: "",
        companyId: "",
        requestId: "",
        page: 1,
      },
      () => this.fetchLogs()
    );
  }

  openModal(log) {
    this.setState({ selectedLog: log, showModal: true });
  }

  closeModal() {
    this.setState({ selectedLog: null, showModal: false });
  }

  statusClass(s) {
    const st = String(s || "").toLowerCase();
    if (st === "done") return "badge badge--done";
    if (st === "error") return "badge badge--error";
    if (st === "dead") return "badge badge--dead";
    if (st === "processing") return "badge badge--processing";
    if (st === "claimed") return "badge badge--claimed";
    return "badge badge--pending";
  }

  fmtDateTime(v) {
    if (!v) return "";
    return String(v).replace("T", " ").slice(0, 19);
  }

  // A row is "stuck" if it's claimed/processing and has been for longer than
  // the same 5-min threshold local-queue-worker now uses to self-reclaim it —
  // if this shows up here for more than a few seconds, something's actually wrong.
  isStuck(log) {
    const st = String(log.status || "").toLowerCase();
    if (st !== "claimed" && st !== "processing") return false;
    const ref = log.startedAt || log.claimedAt;
    if (!ref) return false;
    const ageMs = Date.now() - new Date(ref).getTime();
    return ageMs > 5 * 60 * 1000;
  }

  healthCardClass(h) {
    const secs = Number(h.secondsSinceHeartbeat);
    if (!Number.isFinite(secs)) return "healthCard healthCard--unknown";
    return secs > 180 ? "healthCard healthCard--stale" : "healthCard healthCard--ok";
  }

  healthDotClass(h) {
    const secs = Number(h.secondsSinceHeartbeat);
    if (!Number.isFinite(secs)) return "healthDot healthDot--unknown";
    return secs > 180 ? "healthDot healthDot--stale" : "healthDot healthDot--ok";
  }

  healthLabel(h) {
    const secs = Number(h.secondsSinceHeartbeat);
    if (!Number.isFinite(secs)) return "sin datos";
    if (secs < 60) return `visto hace ${secs}s`;
    return `visto hace ${Math.floor(secs / 60)}min`;
  }

  nextPage() {
    this.setState({ page: this.state.page + 1 }, () => this.fetchLogs());
  }

  prevPage() {
    const p = this.state.page - 1;
    this.setState({ page: p < 1 ? 1 : p }, () => this.fetchLogs());
  }

  renderHealthStrip() {
    const { health, healthError } = this.state;

    // Always show cards for the two processes we know about, even if they've
    // never reported (e.g. brand new deploy) — "sin datos" is itself useful info.
    const known = ["local-queue-worker", "local-monitor-worker"];
    const byName = {};
    health.forEach((h) => { byName[h.process_name] = h; });

    return (
      <div className="card">
        <div className="healthStrip">
          {healthError ? <div className="errorBox">{healthError}</div> : null}
          {known.map((name) => {
            const h = byName[name] || { process_name: name, secondsSinceHeartbeat: null };
            return (
              <div key={name} className={this.healthCardClass(h)}>
                <span className={this.healthDotClass(h)} />
                <div>
                  <div className="healthName">{name}</div>
                  <div className="healthMeta">{this.healthLabel(h)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderModal() {
    if (!this.state.showModal || !this.state.selectedLog) return null;

    const log = this.state.selectedLog;

    return (
      <div className="modalOverlay" onClick={() => this.closeModal()}>
        <div className="modalCard" onClick={(e) => e.stopPropagation()}>
          <div className="modalHeader">
            <div>
              <div className="modalTitle">Log detail</div>
              <div className="modalSub">
                <span className={this.statusClass(log.status)}>{log.status}</span>
                <span className="mono small"> {log.requestId}</span>
              </div>
            </div>
            <button className="btn btn--ghost" onClick={() => this.closeModal()}>
              Close
            </button>
          </div>

          <div className="modalBody">
            <div className="grid2">
              <div className="kv">
                <div className="k">Company</div>
                <div className="v">{log.companyId}</div>
              </div>
              <div className="kv">
                <div className="k">Action</div>
                <div className="v">{log.action}</div>
              </div>
              <div className="kv">
                <div className="k">Requested By</div>
                <div className="v">{log.requestedBy || "—"}</div>
              </div>
              <div className="kv">
                <div className="k">Attempts</div>
                <div className="v">{log.attempts}</div>
              </div>
              <div className="kv">
                <div className="k">Priority</div>
                <div className="v">{log.priority}</div>
              </div>

              <div className="kv">
                <div className="k">Claimed</div>
                <div className="v">{this.fmtDateTime(log.claimedAt)}</div>
              </div>
              <div className="kv">
                <div className="k">Started</div>
                <div className="v">{this.fmtDateTime(log.startedAt)}</div>
              </div>
              <div className="kv">
                <div className="k">Finished</div>
                <div className="v">{this.fmtDateTime(log.finishedAt)}</div>
              </div>
              <div className="kv">
                <div className="k">Ms</div>
                <div className="v">{log.processingMs || ""}</div>
              </div>
            </div>

            <div className="section">
              <div className="sectionTitle">Error</div>
              <pre className="pre">{log.errorMessage || ""}</pre>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading, error, logs } = this.state;

    return (
      <div className="page">
        <Menu Refresh={this.state.Refresh} Father={this} />

        <div className="topBar" style={{ marginTop: 20 }}>
          <div>
            <div className="title">Queue Logs (v3)</div>
            <div className="subtitle">Monitor de procesos del servidor local — Mayaland y Altitude</div>
          </div>

          <div className="topActions">
            <button className="btn" onClick={() => { this.fetchLogs(); this.fetchHealth(); }}>
              Refresh
            </button>

            <label className="switch">
              <input
                type="checkbox"
                checked={this.state.autoRefresh}
                onChange={(e) => this.setAutoRefresh(e.target.checked)}
              />
              <span className="slider" />
              <span className="switchLabel">Auto</span>
            </label>

            <input
              className="input input--small"
              type="number"
              min="5"
              max="120"
              value={this.state.autoRefreshSec}
              onChange={(e) => this.setState({ autoRefreshSec: e.target.value })}
              disabled={!this.state.autoRefresh}
              title="Seconds"
            />
          </div>
        </div>

        {this.renderHealthStrip()}

        <div className="card">
          <div className="filters">
            <div className="field">
              <div className="label">Start</div>
              <input
                className="input"
                type="date"
                value={this.state.dateStart}
                onChange={(e) => this.onChangeField("dateStart", e.target.value)}
              />
            </div>

            <div className="field">
              <div className="label">End</div>
              <input
                className="input"
                type="date"
                value={this.state.dateEnd}
                onChange={(e) => this.onChangeField("dateEnd", e.target.value)}
              />
            </div>

            <div className="field">
              <div className="label">Status</div>
              <select
                className="input"
                value={this.state.status}
                onChange={(e) => this.onChangeField("status", e.target.value)}
              >
                <option value="">All</option>
                <option value="pending">pending</option>
                <option value="claimed">claimed</option>
                <option value="processing">processing</option>
                <option value="done">done</option>
                <option value="error">error</option>
                <option value="dead">dead</option>
              </select>
            </div>

            <div className="field">
              <div className="label">Action</div>
              <input
                className="input"
                placeholder="e.g. createSalesOrder"
                value={this.state.action}
                onChange={(e) => this.onChangeField("action", e.target.value)}
              />
            </div>

            <div className="field">
              <div className="label">Company</div>
              <input
                className="input"
                placeholder="e.g. 3"
                value={this.state.companyId}
                onChange={(e) => this.onChangeField("companyId", e.target.value)}
              />
            </div>

            <div className="field">
              <div className="label">RequestId contains</div>
              <input
                className="input"
                placeholder="q_..."
                value={this.state.requestId}
                onChange={(e) => this.onChangeField("requestId", e.target.value)}
              />
            </div>

            <div className="filterActions">
              <button className="btn" onClick={() => this.applyFilters()}>
                Apply
              </button>
              <button className="btn btn--ghost" onClick={() => this.clearFilters()}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="tableHeader">
            <div className="tableTitle">Logs</div>
            <div className="tableMeta">
              Page <b>{this.state.page}</b> · Showing <b>{logs.length}</b>
              {loading ? <span className="dotLoading">•</span> : null}
            </div>
          </div>

          {error ? <div className="errorBox">{error}</div> : null}

          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                  <th>Company</th>
                  <th>Requested By</th>
                  <th>Attempts</th>
                  <th>Ms</th>
                  <th>RequestId</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="empty">
                      {loading ? "Loading..." : "No logs found"}
                    </td>
                  </tr>
                ) : null}

                {logs.map((log) => (
                  <tr key={log.id || log.requestId} style={this.isStuck(log) ? { background: 'rgba(239,68,68,.06)' } : undefined}>
                    <td>
                      <span className={this.statusClass(log.status)}>{log.status}</span>
                      {this.isStuck(log) ? <span className="badge badge--error" style={{ marginLeft: 6 }}>stuck?</span> : null}
                    </td>
                    <td className="mono">{this.fmtDateTime(log.createdAt)}</td>
                    <td>{log.action}</td>
                    <td className="mono">{log.companyId}</td>
                    <td className="small">{log.requestedBy || "—"}</td>
                    <td className="mono">{log.attempts}</td>
                    <td className="mono">{log.processingMs || ""}</td>
                    <td className="mono small">{log.requestId}</td>
                    <td className="right">
                      <button className="btn btn--ghost" onClick={() => this.openModal(log)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button className="btn btn--ghost" onClick={() => this.prevPage()} disabled={this.state.page <= 1 || loading}>
              Prev
            </button>
            <button className="btn btn--ghost" onClick={() => this.nextPage()} disabled={loading || logs.length < this.state.pageSize}>
              Next
            </button>
          </div>
        </div>

        {this.renderModal()}
      </div>
    );
  }
}
